<script setup>
import {computed, ref, watch} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useModalsStore} from '@/stores/modals'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'

import ItemHistoryChangeTypeCheckbox from './ItemHistoryChangeTypeCheckbox.vue'
import ItemHistoryChangeTypeCodeEditor from './ItemHistoryChangeTypeCodeEditor.vue'
import ItemHistoryChangeTypeCustomStatus from './ItemHistoryChangeTypeCustomStatus.vue'
import ItemHistoryChangeTypeDate from './ItemHistoryChangeTypeDate.vue'
import ItemHistoryChangeTypeDateTimeLocal from './ItemHistoryChangeTypeDateTimeLocal.vue'
import ItemHistoryChangeTypeFile from './ItemHistoryChangeTypeFile.vue'
import ItemHistoryChangeTypeInternalLink from './ItemHistoryChangeTypeInternalLink.vue'
import ItemHistoryChangeTypeJson from './ItemHistoryChangeTypeJson.vue'
import ItemHistoryChangeTypePassword from './ItemHistoryChangeTypePassword.vue'
import ItemHistoryChangeTypeTexteditor from './ItemHistoryChangeTypeTexteditor.vue'
import ItemHistoryChangeTypeRelationshipManyToMany from './ItemHistoryChangeTypeRelationshipManyToMany.vue'
import ItemHistoryChangeTypeRelationshipOneToMany from './ItemHistoryChangeTypeRelationshipOneToMany.vue'
import Button from '@/common/Button.vue'

const props = defineProps({
    formId: String,
    tableName: String,
    id: Number,
    property: String,
    newValue: [String, Number],
    oldValue: [String, Number]
})

// Pinia Stores
const componentsStore = useComponentsStore()
const modalsStore = useModalsStore()
const componentStructureStore = useComponentStructureStore()
const itemDataStore = useItemDataStore()

// Local Reactive State replacing useAsyncState
const contentItem = ref(null)

// Watch tableName and id to load contentItem natively
watch(
    () => [props.tableName, props.id],
    async ([newTable, newId]) => {
        if (newTable === 'content' && newId) {
            contentItem.value = await itemDataStore.get({
                tableName: 'content',
                id: newId
            })
        } else {
            contentItem.value = null
        }
    },
    {immediate: true}
)

// --- Computed Properties ---
const componentId = computed(() => {
    // If this is the Content component then the property may relate to a value stored in the content type's
    // component e.g. component_123.
    if (
        props.tableName === 'content'
        && contentItem.value
    ) {
        const tableName = 'component_' + contentItem.value.contentType
        const targetComponentId = componentsStore.componentIds[tableName]

        // Check if the property relates to one of the CT's fields. If it does then by overriding the
        // componentId with the content type component's this.column, this.type will source the CTs field data.
        const field = componentStructureStore.get(targetComponentId)
            ?.find(o => o.columnName === props.property)

        if (field) {
            return targetComponentId
        }
    }

    return componentsStore.componentIds[props.tableName]
})

const column = computed(() => {
    if (!componentId.value) return null
    return componentStructureStore.get(componentId.value)
        ?.find(o => o.columnName === props.property)
})

const type = computed(() => {
    switch (props.property) {
        case 'isArchived':
            return 'checkbox'

        case 'tags':
            return props.property
    }

    if (column.value) {
        const typeVal = column.value.type

        if (
            typeVal === 'checkbox'
            && column.value.columnName === 'status'
        ) {
            return 'status'
        }

        return typeVal
    }
    return null
})

const label = computed(() => {
    switch (props.property) {
        case 'isArchived':
            return 'Archived'

        case 'tags':
            return 'Tags'
    }

    if (column.value) {
        return column.value.title
    }
    return ''
})

const componentName = computed(() => {
    const typeComponentNames = {
        status: ItemHistoryChangeTypeCustomStatus,
        checkbox: ItemHistoryChangeTypeCheckbox,
        codeEditor: ItemHistoryChangeTypeCodeEditor,
        date: ItemHistoryChangeTypeDate,
        'datetime-local': ItemHistoryChangeTypeDateTimeLocal,
        file: ItemHistoryChangeTypeFile,
        image: ItemHistoryChangeTypeFile,
        internalLink: ItemHistoryChangeTypeInternalLink,
        json: ItemHistoryChangeTypeJson,
        password: ItemHistoryChangeTypePassword,
        relationshipOneToMany: ItemHistoryChangeTypeRelationshipOneToMany,
        relationshipManyToMany: ItemHistoryChangeTypeRelationshipManyToMany,
        texteditor: ItemHistoryChangeTypeTexteditor,
    }

    return typeComponentNames[type.value] || null
})

const componentProps = computed(() => {
    const computedProps = {
        oldValue: props.oldValue,
        newValue: props.newValue
    }

    switch (type.value) {
        case 'checkbox':
        case 'file':
        case 'image':
        case 'internalLink':
        case 'json':
        case 'status':
            computedProps.value = props.oldValue
            break

        case 'codeEditor':
            computedProps.value = props.oldValue
            computedProps.mode = column.value?.codeEditorMode
            computedProps.label = label.value
            break

        case 'date':
            computedProps.value = props.oldValue
            computedProps.dateFormat = column.value?.dateFormat
            break

        case 'relationshipOneToMany':
        case 'relationshipManyToMany':
            computedProps.value = props.oldValue
            computedProps.categoryComponentId = column.value?.categoryComponentId
            break

        case 'texteditor':
            computedProps.value = props.oldValue
            computedProps.label = label.value
            break
    }

    return computedProps
})

// --- Methods ---
const restore = (property, value) => {
    const modalPromise = modalsStore.show({
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Are you sure?',
            modalBody: `The value being replaced will be saved to the item's history.`,
            onConfirm: () => {
                itemDataStore.update({
                        tableName: props.tableName,
                        id: props.id,
                        data: {
                            [property]: value
                        }
                    })
                    .then(() => {
                        const formStore = useFormStore(props.formId)

                        formStore.presetData({
                            name: property,
                            value: value,
                        })

                        // Toggle the form's fieldsets so that dynamic fields are refreshed else, for example,
                        // editors and date fields won't update.
                        formStore.toggleShow()

                        modalPromise.then((obj) => {
                            modalsStore.remove(obj.index)
                        })
                    })
            }
        }
    })
}
</script>

<template>
    <div v-if="type" class="d-flex align-items-top">
        <small class="mt-1 me-2 w-25">{{ label }}</small>

        <div class="w-75">
            <component v-bind="componentProps" :is="componentName"/>

            <template v-if="!componentName && type !== 'hidden'">
                <div v-if="tableName === 'pages' && property === 'content'">
                    <div class="badge bg-secondary">changed</div>
                </div>

                <div v-else class="d-flex align-items-center">
                    <small v-if="oldValue" class="border rounded py-1 px-2" v-html="oldValue"></small>
                    <i v-else class="small">empty</i>

                    <i class="mx-2 bi-chevron-right"/>

                    <small v-if="newValue" class="border rounded py-1 px-2" v-html="newValue"></small>
                    <i v-else class="small">empty</i>
                </div>
            </template>
        </div>

        <button v-if="type !== 'password'"
                class="position-absolute end-0 top-0 mt-2 me-2 btn btn-secondary btn-sm visible-on-hover-hidden"
                type="button" @click="restore(property, oldValue)">
            restore
        </button>
    </div>
</template>